import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Card,
  CardContent,
  Paper,
  Button,
  Chip,
  InputAdornment,
  IconButton,
  Divider
} from "@mui/material";
import { Settings, Refresh, Search, BugReport } from "@mui/icons-material";
import { LoaderComponentProps, withLoader } from "../../../AppComponents/Loader";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { AIServiceSettingsActions } from "../../../Store/Slices/Settings/AIServiceSettings/aiServiceSettings.actions";
import { aiServiceSettings, aiModels, apiKeyData, openRouterSettings } from "../../../Store/Slices/Settings/AIServiceSettings/aiServiceSettings.selectors";
import { AIServiceSettingsWrapper, ContentWrapper } from "./AIServiceSettingsStyles";
import { ResponseHeader } from "../ChatSettings/CannedResponse/CannedResponsesstyles";
import MotionPhotosAutoIcon from '@mui/icons-material/MotionPhotosAuto';
import AppButton from "../../../AppComponents/AppButton";
import { useNotification } from "../../../Hooks/useNotification/useNotification";
import AppNoDataLoader from "../../../AppComponents/AppNoDataLoader";
import { AppMaterialIcons } from "../../../AppComponents/AppMaterialIcons";
import AppPaginationComponent from "../../../AppComponents/Pagination/Pagination";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { useCurrentBot } from "../../../Store/Slices/DashboardSlices";
import { WorkspaceActions } from "../../../Store/Slices/Settings/LiveChat/Workspace/workspace.actions";
import { AppForm, AppFormSwitchField } from "../../../AppComponents/Forms";
import { CoreService } from "../../../Services/Apis/CoreApis";
import { setConfigData } from "../../../Store/Slices/AuthSlice";

interface Props extends LoaderComponentProps { }

const AIServiceSettings: React.FC<Props> = ({ setLoading }) => {
  const dispatch = useAppDispatch();

  const currentSettings = useAppSelector(aiServiceSettings);
  const models = useAppSelector(aiModels);
   const {bot_id} = useAppSelector(useCurrentBot);
  let { showAlert } = useNotification();
  const [serviceType, setServiceType] = useState("openrouter");
  const [apiKey, setApiKey] = useState("");
  const [autoRouter, setAutoRouter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [defaultModel, setDefaultModel] = useState<string>("");

  // Separate selected models for each service type
  const [openRouterSelectedModel, setOpenRouterSelectedModel] = useState("");
  const [directSelectedModel, setDirectSelectedModel] = useState("");

  const [providerFilter, setProviderFilter] = useState("all");
  const [apiKeyDataState, setApiKeyDataState] = useState<any>(null);
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Master list of all models for client-side filtering
  const [allModels, setAllModels] = useState<any[]>([]);
  const [openRouterModels, setOpenRouterModels] = useState<any[]>([]);
  const [openAiModels, setOpenAiModels] = useState<any[]>([]);
  const [aiConfigModel, setAiConfigModel] = useState<any>({});
  const [allProvider, setAllProvider] = useState<any>();


  const apiKeyResponse = useAppSelector(apiKeyData);
  const openRouterData = useAppSelector(openRouterSettings);
  const location = useLocation();
  const isAIServiceBot = location.pathname.includes("ai_service_settings_bot");

  const [localValues, setLocalValues] = useState<any>({
    itemsPerPage: { label: "10 per page", value: 10 },
    currentPage: 0,
  });

  // Helper function to get current selected model
  const getSelectedModel = () => serviceType === "openrouter" ? openRouterSelectedModel : directSelectedModel;
  const selectedModel = getSelectedModel();

  const setSelectedModelForCurrentMode = (value: string) => {
    if (serviceType === "openrouter") {
      setOpenRouterSelectedModel(value);
    } else {
      setDirectSelectedModel(value);
    }
  };
  // Fetch data based on service type
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        // For Smart AI LLM - fetch from /workspace/openrouter
        const openRouterResponse = await dispatch(
          AIServiceSettingsActions.getOpenRouterSettings({})
        ).unwrap();

        console.log("OpenRouter Response:", openRouterResponse);
        const openRouterDatas = openRouterResponse?.data;
        const openRouterModelsList = openRouterDatas?.available_models?.models || [];
        const openAiModelsList = openRouterDatas?.available_models_for_open_ai?.models || [];
        setOpenRouterModels(openRouterModelsList);
        setOpenAiModels(openAiModelsList);
        // Get saved configuration
        const savedConfig = openRouterDatas?.current_config?.config_value || {};
        setAiConfigModel(savedConfig);
        // Set auto router state
        setAutoRouter(openRouterDatas?.auto_router_enabled || openRouterDatas?.auto_router || false);
        // Load saved model and service type from config
        if (savedConfig?.model) {
          const savedModel = savedConfig.model;
          const savedServiceType = savedConfig.service_type || "openrouter";
          const savedApiKey = savedConfig.openai_api_key || "";

          // Set the selected model based on service type
          if (savedServiceType === "openrouter") {
            setOpenRouterSelectedModel(savedModel);
          } else if (savedServiceType === "client") {
            setDirectSelectedModel(savedModel);
            setApiKey(savedApiKey);
          }

          console.log("Loaded saved config:", savedConfig);
        }
        // Reset unsaved changes flag after loading config
        setHasUnsavedChanges(false);
        // Get all unique providers with their counts
        const providerCounts: { [key: string]: number } = {};
        openRouterModelsList?.forEach((model: any) => {
          const provider = model.provider;
          providerCounts[provider] = (providerCounts[provider] || 0) + 1;
        });
        const providersWithCount = Object.keys(providerCounts).map(provider => ({
          name: provider,
          count: providerCounts[provider]
        }));

        setAllProvider(providersWithCount);

      } catch (error) {
        console.error("Failed to load data:", error);
        if (isMounted) showAlert("Failed to load data", "error");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false };
  }, [dispatch, showAlert]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update allModels when serviceType changes
  useEffect(() => {
    if (serviceType === "openrouter") {
      setAllModels(openRouterModels);
    } else {
      setAllModels(openAiModels);
    }
  }, [serviceType, openRouterModels, openAiModels]);

  // Sync service type from saved config
  useEffect(() => {
    if (aiConfigModel?.service_type) {
      const savedServiceType = aiConfigModel.service_type === "client" ? "direct" : "openrouter";
      if (savedServiceType !== serviceType) {
        setServiceType(savedServiceType);
      }
    }
  }, [aiConfigModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle API key response for direct mode
  useEffect(() => {
    if (apiKeyResponse && serviceType === "direct") {
      const savedModel = (apiKeyResponse as any).model || "";
      const savedKey = (apiKeyResponse as any).key_suffix || "";

      if (savedModel && savedModel !== directSelectedModel) {
        setDirectSelectedModel(savedModel);
      }
      if (savedKey && savedKey !== apiKey) {
        setApiKey(savedKey);
      }
      setApiKeyDataState(apiKeyResponse);
    }
  }, [apiKeyResponse, serviceType]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOpenRouterSettings = async () => {


    setLoading(true);
    try {
      const response = await dispatch(AIServiceSettingsActions.saveOpenRouterSettings({
        "model": selectedModel,
        "openai_api_key": apiKey,
        "service_type": serviceType === "direct" ? "client" : "openrouter"

      })).unwrap();

      if (response?.status === 200) {
        showAlert(response?.data?.message || "OpenRouter settings saved successfully!", "success");
        let data = response?.data;
        setAiConfigModel(data?.config_value)
        setHasUnsavedChanges(false);
      } else {
        showAlert(response?.data?.error || "Failed to save settings", "error");
      }
    } catch (error: any) {
      console.error("Failed to save OpenRouter settings:", error);
      showAlert(error?.message || "Failed to save OpenRouter settings. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }
  const getFilteredModels = () => {
    const sourceModels = serviceType === "openrouter" ? openRouterModels : openAiModels;
    console.log("Source Models:", sourceModels);

    if (!sourceModels || !Array.isArray(sourceModels) || sourceModels.length === 0) return [];

    let filtered = [...sourceModels];

    // Search filter - CLIENT-SIDE ONLY
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((model: any) =>
        model.name?.toLowerCase().includes(query) ||
        model.provider?.toLowerCase().includes(query) ||
        model.normalKey?.toLowerCase().includes(query)
      );
    }

    // Provider filter - CLIENT-SIDE ONLY
    if (providerFilter !== "all") {
      filtered = filtered.filter((model: any) => model.provider === providerFilter);
    }
    console.log("Filtered Models:", filtered);

    return filtered;
  };
  function handlePageChange(event: any, value: any) {
    setLocalValues({
      ...localValues,
      currentPage: value - 1,
    });
  }
  const handleChangeRowsPerPage = (event: any) => {
    setLocalValues({
      ...localValues,
      itemsPerPage: event,
      currentPage: 0,
    });
  };

  const toggleModelSelection = (modelKey: string) => {
    const currentSelected = getSelectedModel();
    if (currentSelected.toLowerCase() === modelKey.toLowerCase()) {
      // If clicking the already selected model, deselect it
      setSelectedModelForCurrentMode("");
    } else {
      // Select the new model (only one can be selected)
      setSelectedModelForCurrentMode(modelKey);
      // Reset to first page when selecting a model so it's visible
      setLocalValues({
        ...localValues,
        currentPage: 0,
      });
    }
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  const handleApiKeySave = async () => {
    if (!selectedModel) {
      showAlert("Please select a model", "warning");
      return;
    }

    const payload = {
      "model": selectedModel,
      "openai_api_key": apiKey,
      "service_type": serviceType === "direct" ? "client" : "openrouter"
    };

    try {
      setLoading(true);
      const response =  await dispatch(AIServiceSettingsActions.saveApiKey(payload)).unwrap?.();
      let data = response?.data;
      // Refresh data after save
      setAiConfigModel(data?.config_value)
      showAlert("API Key saved successfully!", "success");
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Failed to save API key:", error);
      showAlert("Failed to save API key. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApiKeyDelete = async () => {
    try {
      setLoading(true);
      const payload = {
        "model": "",
        "openai_api_key": "",
        "service_type": serviceType === "direct" ? "client" : "openrouter",
        "is_delete_key" : true
    };
      const response =  await dispatch(AIServiceSettingsActions.saveApiKey(payload)).unwrap?.(); 
      let data = response?.data;
      console.log("data-------------->",data);
      setAiConfigModel(data?.config_value)
      setSelectedModelForCurrentMode("");
      setApiKey("");
      setApiKeyDataState(null);
      // Refresh data
      // dispatch(AIServiceSettingsActions.getApiKey({}));
      showAlert("API Key deleted successfully!", "success");
    } catch (error) {
      console.error("Failed to delete API key:", error);
      showAlert("Failed to delete API key. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // const allModels = getallModels();
  // const freeModelsCount = Array.isArray(allModels) ? allModels.filter((m: any) =>
  //   m.pricing?.input_token_price === 0 || m.pricing?.input_token_price === "0"
  // ).length : 0;

  // const uniqueProviders = Array.isArray(allModels) && allModels.length > 0 ?
  //   Array.from(new Set(allModels.map((m: any) => m.provider))).sort() : [];

  // console.log("All Models:", allModels);
  // console.log("Filtered Models:", allModels);
  // console.log("Selected Model:", selectedModel);
  console.log("allModels", allModels);


  const [sentimentIntentValue, setSentimentIntentValue] = useState<boolean>(false);

  const HEADERS = {
    appid: JSON.stringify({
      botid: bot_id,
    }),
  };
  
  const getWorkspaceData = () => {
    const data = {
      HEADERS,
    };
    dispatch(WorkspaceActions.getWorkspaceConfigData(data)).then((res: any) => {
      if (res?.payload?.status === 200) {
        const data = res?.payload?.data?.data;
        setSentimentIntentValue(data?.enable_sentiment_intent_analytics);
      } else {
        showAlert("Something went wrong", "error");
      }
    });
  };

  useEffect(() => {
    getWorkspaceData();
  }, []);
   useEffect(() => {
    setLocalValues((prev: any) => ({
      ...prev,
      currentPage: 0
    }));
  }, [searchQuery, providerFilter, serviceType]);

  const handleSentimentIntentSubmit = async (values: any, submitProps: any) => {
    console.log("🚀 ~ handleSentimentIntentSubmit ~ values:", values)
    try {
      const payload = {
        "enable_sentiment_intent_analytics": values?.enable_sentiment_intent_analytics
      }
      const data = {
        payload,
        HEADERS
      }
     dispatch(WorkspaceActions.updateWokspaceConfigData(data)).then((response) =>{
        if(response?.payload?.status === 200){
          showAlert("Settings saved successfully", "success");
        }
     })
     .catch((error: any) => {
        showAlert("Failed to save settings", "error");
     })
    } catch (error) {
      showAlert("Failed to save settings", "error");
    } finally {
      submitProps.setSubmitting(false);
      CoreService.tokenVerify()
        .then((res: any) => {
          if (res.data) {
            if (res.data.status) {
              dispatch(setConfigData({ payload: res?.data?.token }));
            }
          }
        })
        .catch((err: any) => {
          console.error(err, "Error Triggered in verify api.........");
        })
    }
  };

  return (
    <AIServiceSettingsWrapper>
      <ResponseHeader style={{ backgroundColor: isAIServiceBot ? "transparent" : "#fff" }}>
        <div className="response-header__left">
          <h4>AI Services</h4>
        </div>
      </ResponseHeader>
      <ContentWrapper>

        {/* AI Services Configuration */}
        <AppForm
          initialValues={{ enable_sentiment_intent_analytics: sentimentIntentValue }}
          onSubmit={handleSentimentIntentSubmit}
          enableReinitialize={true}
        >
        <Card sx={{ mb: 3 }}>
          <CardContent>
              <TopContainer>
                <div>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  AI Services Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure your AI providers and model preferences
                </Typography>
                    </div>
                    <div>
                      <AppFormSwitchField label="Enable user sentiment and intent analytics" tooltip={"Captures user intent and sentiment from bot conversations to support smarter bot interactions."} name="enable_sentiment_intent_analytics" submitOnChange={true} />
                    </div>
                  </TopContainer>
            {/* Provider Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
              <Paper
                variant="outlined"
                onClick={() => setServiceType("openrouter")}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: serviceType === "openrouter" ? '2px solid' : '1px solid',
                  borderColor: serviceType === "openrouter" ? 'primary.main' : 'divider',
                  bgcolor: serviceType === "openrouter" ? 'primary.50' : 'transparent',
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Settings sx={{ color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Smart AI LLM
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Access 100+ models from multiple providers
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper
                variant="outlined"
                onClick={() => setServiceType("direct")}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: serviceType === "direct" ? '2px solid' : '1px solid',
                  borderColor: serviceType === "direct" ? 'primary.main' : 'divider',
                  bgcolor: serviceType === "direct" ? 'primary.50' : 'transparent',
                  '&:hover': { borderColor: 'primary.main' },
                  // opacity: autoRouter ? 0.5 : 1,
                  // pointerEvents: autoRouter ? 'none' : 'auto'
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'success.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Settings sx={{ color: 'success.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Configure your Own OpenAI Key
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Connect directly to AI providers
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* API Configuration */}
            {serviceType === "direct" && (
              <>
                {/* Show message if Smart AI model is already configured */}
                {aiConfigModel?.service_type === "openrouter" && aiConfigModel?.model && !aiConfigModel?.openai_api_key && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      mb: 2,
                      bgcolor: 'info.50',
                      borderColor: 'info.200',
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'info.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Settings sx={{ color: 'info.main', fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'info.dark' }}>
                        Smart AI Model Already Configured
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        You are already using the <strong>{aiConfigModel?.model}</strong> model from Smart AI LLM.
                        If you want to use your own OpenAI API key instead, please unselect the Smart AI model first by going to the
                        <strong> "Smart AI LLM"</strong> tab and removing your selection.
                      </Typography>
                    </Box>
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Settings sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      API Configuration
                    </Typography>
                    {hasUnsavedChanges && (
                      <Chip
                        label="Unsaved Changes"
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        OpenAI Key
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="text"
                        value={
                          apiKey.length > 3
                            ? "•".repeat(apiKey.length - 3) + apiKey.slice(-3) // show only last 3 chars
                            : apiKey
                        }
                        onChange={(e) => {
                          const input = e.target.value;
                          setApiKey(input);
                          setHasUnsavedChanges(true);
                        }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1.5px solid #e0e0e0',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 3px rgba(91, 115, 232, 0.05)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 4px rgba(91, 115, 232, 0.15)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            color: 'text.primary',
                          },
                        }}
                        placeholder="Enter your API key"
                      />
                      {apiKey && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Model: {selectedModel || "Not selected"}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        Model Name
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={selectedModel}
                        onChange={(e) => {
                          setSelectedModelForCurrentMode(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                        SelectProps={{
                          native: true,
                          sx: {
                            '& option': {
                              padding: '10px 12px',
                              fontSize: '0.9rem',
                            },
                          },
                        }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1.5px solid #e0e0e0',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 3px rgba(91, 115, 232, 0.05)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              boxShadow: '0 0 0 4px rgba(91, 115, 232, 0.15)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            color: 'text.primary',
                          },
                        }}
                      >
                        <option value="">Select Model</option>
                        {openAiModels.map((model: any) => (
                          <option key={model.normalKey} value={model.normalKey}>
                            {model.name}
                          </option>
                        ))}
                      </TextField>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                    {!aiConfigModel?.openai_api_key ? (
                      <AppButton
                        onClick={handleApiKeySave}
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        disabled={!hasUnsavedChanges}
                      >
                        Save
                      </AppButton>
                    ) : (
                      <>
                        <AppButton onClick={handleApiKeyDelete} variant="danger-filled" sx={{ textTransform: 'none' }}>
                          <AppMaterialIcons iconName="DeleteOutlineOutlined" />
                          Delete
                        </AppButton>
                        <AppButton
                          onClick={handleApiKeySave}
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                          disabled={!hasUnsavedChanges}
                        >
                          Update
                        </AppButton>

                      </>
                    )}
                  </Box>
                </Paper>
              </>
            )}

            {/* Auto Router */}
            {isAIServiceBot && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'secondary.50',
                  borderColor: 'secondary.200',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#EEF1FF',
                  opacity: serviceType === "direct" ? 0.5 : 1,
                  pointerEvents: serviceType === "direct" ? 'none' : 'auto',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: 'secondary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <MotionPhotosAutoIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Auto Router Config
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Let OpenRouter automatically choose the best model for each request
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={autoRouter}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setAutoRouter(newValue);
                    try {
                      let response = await dispatch(AIServiceSettingsActions.saveOpenRouterSettings({
                        // auto_router: newValue
                        "model": selectedModel,
                        "openai_api_key": apiKey,
                        "service_type": serviceType === "direct" ? "client" : "openrouter"
                      })).unwrap?.();
                      let data = response?.data;
                      setAiConfigModel(data?.config_value)
                    } catch (error) {
                      console.error("Failed to save OpenRouter settings:", error);
                      setAutoRouter(!newValue); // Revert on error
                    }
                  }}
                  color="primary"
                />
              </Paper>
            )}
            {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Note :-</strong>
              <br />   If no model or API key is provided, or if Auto Router is turned off, the system will automatically use <strong>GPT-3.5-Turbo</strong>.
              <br />The selected model applies only to the <strong>Smart Reply Assistant (Inbox)</strong> and the <strong>AI Template Generator</strong>.
            </Typography> */}
            {/* Notes Section */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mt: 2,
                bgcolor: 'linear-gradient(135deg, rgba(91, 115, 232, 0.08) 0%, rgba(91, 115, 232, 0.04) 100%)',
                borderColor: 'primary.100',
                borderLeft: '4px solid',
                borderLeftColor: 'primary.main',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Settings sx={{ fontSize: 18, color: 'primary.main' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      color: 'primary.main',
                      mb: 1.5,
                      fontSize: '0.95rem',
                    }}
                  >
                    📋 Important Notes
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Typography
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          lineHeight: 1,
                          mt: 0.2,
                        }}
                      >
                        •
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        If no model or API key is provided, the system will automatically use{' '}
                        <Typography component="span" variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
                          gpt-4o-mini
                        </Typography>
                        .
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Typography
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          lineHeight: 1,
                          mt: 0.2,
                        }}
                      >
                        •
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        The selected model applies only to the{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => window.open('/conversations/chats', '_blank')}
                        >
                          Smart Reply Assistant (Inbox)
                        </Typography>
                        {' '}and the{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => window.open('/broadcast/whatsapp/create-template?source=generateNow', '_blank')}
                        >
                          AI Template Generator
                        </Typography>
                        .
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </CardContent>
        </Card>
        </AppForm>

        {/* Enhanced Model Selection */}
        {serviceType === "openrouter" && (

          <Card sx={{ position: 'relative' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings sx={{ fontSize: 20 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Enhanced Model Selection & Search
                  </Typography>
                  {selectedModel ? (
                    <Chip
                      label={`Selected: ${selectedModel}`}
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  ):       <Chip
                    label={`No Model Selected`}
                    color="warning"
                    size="small"
                    sx={{ ml: 1 }}
                  />}
                  {hasUnsavedChanges && (
                    <Chip
                      label="Unsaved Changes"
                      color="warning"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={saveOpenRouterSettings}
                  sx={{ textTransform: 'none' }}
                  disabled={!hasUnsavedChanges}
                >
                  Save
                </Button>
              </Box>

              {/* Blur overlay when custom key is configured */}
              {aiConfigModel?.openai_api_key && aiConfigModel?.service_type === "client" && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1,
                  }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      maxWidth: 500,
                      textAlign: 'center',
                      bgcolor: 'white',
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'warning.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                      }}
                    >
                      <Settings sx={{ fontSize: 32, color: 'warning.main' }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Custom API Key Configured
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      You have configured your own API key. To select AI models from this section,
                      please delete your custom key configuration first by going to the
                      "Configure your Own OpenAI Key" tab.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setServiceType("direct")}
                      sx={{ textTransform: 'none' }}
                    >
                      Go to API Key Settings
                    </Button>
                  </Paper>
                </Box>
              )}

              {/* Filters Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: 'grey.50',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Provider Filter */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '0 1 auto' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        letterSpacing: 0.3,
                      }}
                    >
                      Filter by Provider:
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={providerFilter}
                      onChange={(e) => setProviderFilter(e.target.value)}
                      SelectProps={{
                        native: true,
                        sx: {
                          '& option': {
                            padding: '10px 12px',
                            fontSize: '0.9rem',
                          },
                        },
                      }}
                      sx={{
                        bgcolor: 'white',
                        minWidth: 300,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: '1.5px solid #e0e0e0',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 3px rgba(91, 115, 232, 0.05)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 4px rgba(91, 115, 232, 0.15)',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          color: 'text.primary',
                        },
                      }}
                    >
                      <option value="all">All Providers ({openRouterModels?.length || 0})</option>
                      {allProvider?.map((provider: any) => (
                        <option key={provider.name} value={provider.name}>
                          {provider.name} ({provider.count})
                        </option>
                      ))}
                    </TextField>
                  </Box>

                  {/* Search field */}
                  <TextField
                    size="small"
                    placeholder="Search models by name, provider..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      bgcolor: 'white',
                      flex: '1 1 300px',
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Results count */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                      ml: 'auto',
                    }}
                  >
                    {getFilteredModels().length} models found
                  </Typography>
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* Model Count */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              </Box>

              {/* Model Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2.5,
                mb: 2,
              }}>
                {(() => {
                  // Get filtered models
                  const filteredModels = getFilteredModels();

                  // Sort models to show selected model first
                  const sortedModels = [...filteredModels].sort((a, b) => {
                    const aSelected = selectedModel.toLowerCase() === (a.normalKey || a.name).toLowerCase();
                    const bSelected = selectedModel.toLowerCase() === (b.normalKey || b.name).toLowerCase();
                    if (aSelected && !bSelected) return -1;
                    if (!aSelected && bSelected) return 1;
                    return 0;
                  });

                  // Apply pagination
                  const itemsPerPage = localValues?.itemsPerPage?.value || 10;
                  const currentPage = localValues?.currentPage || 0;
                  const startIndex = currentPage * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;

                  return sortedModels.slice(startIndex, endIndex);
                })().map((model: any) => {
                  const isSelected = selectedModel.toLowerCase() === (model.normalKey || model.name).toLowerCase();
                  const isFree = model.input_token_price === 0 || model.input_token_price === "0";

                  return (
                    <Card
                      key={model.normalKey || model.name}
                      variant="outlined"
                      sx={{
                        transition: 'all 0.3s ease',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          transition: 'all 0.3s ease',
                        },
                        '&:hover': {
                          borderColor: isSelected ? 'primary.main' : 'primary.light',
                          boxShadow: isSelected
                            ? '0 8px 16px rgba(91, 115, 232, 0.2)'
                            : '0 4px 12px rgba(0, 0, 0, 0.08)',
                          transform: 'translateY(-2px)',
                        },
                        bgcolor: isSelected ? 'primary.50' : 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <CardContent sx={{ pb: 2 }}>
                        {/* Header with name and provider badge */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              flex: 1,
                              color: isSelected ? 'primary.main' : 'text.primary',
                              fontSize: '0.95rem',
                              lineHeight: 1.3,
                            }}
                          >
                            {model.name}
                          </Typography>
                          <Chip
                            label={model.provider}
                            size="small"
                            variant={isSelected ? "filled" : "outlined"}
                            color={isFree ? 'primary' : 'default'}
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          />
                        </Box>

                        {/* Pricing information */}
                        <Box sx={{ mb: 2.5, bgcolor: 'grey.50', p: 1.5, borderRadius: 1, }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Input Price
                              </Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: isFree ? 'success.main' : 'text.primary', mt: 0.3 }}>
                                {isFree ? 'Free' : `$${parseFloat(model.input_token_price).toFixed(3)}/M`}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Output Price
                              </Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary', mt: 0.3 }}>
                                ${parseFloat(model.output_token_price).toFixed(3)}/M
                              </Typography>
                            </Box>
                          </Box>
                          {/* {model.cached_input_token_price && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Cached Price
                              </Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary', mt: 0.3 }}>
                                ${parseFloat(model.cached_input_token_price).toFixed(3)}/M
                              </Typography>
                            </Box>
                          )} */}
                        </Box>

                        {/* Select button */}
                        <Button
                          fullWidth
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            toggleModelSelection(model.normalKey || model.name);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            borderRadius: 1,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.02)',
                            },
                          }}
                        >
                          {isSelected ? '✓ Selected' : 'Select Model'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              {getFilteredModels().length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <AppNoDataLoader />
                </Paper>
              )}
              {getFilteredModels().length > 0 && (
                <AppPaginationComponent
                  totalCount={getFilteredModels().length}
                  handlePageChange={handlePageChange}
                  currentPage={localValues?.currentPage}
                  totalPages={Math.ceil(getFilteredModels().length / (localValues?.itemsPerPage?.value || 10))}
                  rowsPerPage={localValues?.itemsPerPage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </CardContent>
          </Card>
        )}
      </ContentWrapper>
    </AIServiceSettingsWrapper>
  );
};

export default withLoader(AIServiceSettings);
const TestNots = styled.div`
    height: auto;
    padding: 13px 18px 23px 19px;
    border-radius: 4px;
    background: rgba(91, 115, 232, 0.15);
`
const TestNoteList = styled.div`
    padding-left: 2rem;
`;


const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-top: 4px;
`;
